import { isDischargeActionField } from '@/utils/noteFieldActions';
// Note enrichment pipeline: carryforward → form population → prefill.
// Extracted from ClinicalNoteEdit so scribe-created notes (utils/visitCreation)
// get the same treatment at creation time. Every stage fills ONLY empty
// fields, so the pipeline is idempotent — running it again on an already
// enriched note is a no-op.

import { apiService } from '@/services/api';
import { THERAPY_EVAL_CPT_CODES, TIMED_CPT_CODES } from '@prism-ehr/shared/utils/cptCodes';
import { applyFormPopulation } from '@/utils/formPopulationMapper';

// Goal grids share field ids across templates but not column names: eval
// templates store rows under "Goal Description" / "Time to Meet", progress,
// daily and discharge templates read "Goal" / "Status" / ... . The editor
// looks each cell up by the TARGET template's column name, so rows copied
// verbatim between differing templates render as empty grids.
const GRID_COLUMN_SYNONYMS = {
  'Goal': ['Goal Description'],
  'Goal Description': ['Goal'],
};

/**
 * Reshape data_grid rows so every cell is keyed by one of the target
 * template's columns. Used by carry-forward and section import.
 *
 * - Rows already keyed by any target column pass through unchanged
 *   (same-template copy, or partial overlap where the shared columns match).
 * - Otherwise each target column takes the source value of the same name,
 *   else a synonym (Goal <-> Goal Description), else ''. "Status" defaults to
 *   '0' (the percent-progress option list) so eval goals land as 0% complete.
 *   Row ids carry over (`id`, else the eval grid's "#" column).
 *
 * @param {any} rows source grid value
 * @param {string[]} targetColumns target field.columns
 */
export function remapGridRowsToColumns(rows, targetColumns) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  if (!Array.isArray(targetColumns) || targetColumns.length === 0) return rows;
  const firstRow = rows[0];
  if (!firstRow || typeof firstRow !== 'object') return rows;
  if (targetColumns.some(col => col in firstRow)) return rows;
  return rows.map(row => {
    const next = {};
    for (const col of targetColumns) {
      let v = row?.[col];
      if (v == null) {
        const alt = (GRID_COLUMN_SYNONYMS[col] || []).find(k => row?.[k] != null);
        v = alt ? row[alt] : undefined;
      }
      if (v == null) v = col === 'Status' ? '0' : '';
      next[col] = v;
    }
    const id = row?.id || row?.['#'];
    if (id !== undefined) next.id = id;
    return next;
  });
}

/**
 * Build a flow_sheet field value from "latest recorded" flow_sheet_entry rows
 * (one row per item + side, newest first — see flow-sheet-entry-latest_GET.xs).
 * Only items configured on the target field are filled; the editor and the
 * entry sync both ignore unconfigured items. Mirrors the shapes the sync writes
 * (ClinicalNoteEdit.syncFlowSheetEntries): bilateral → { left, right }, plain →
 * { value }, multi-select → value_json.selectedValues, WNL/WFL-only → the
 * status stored as the value with value_json.status, outcome component scores
 * in value_json.componentScores/componentLabels.
 *
 * @returns {Object|null} flow sheet value, or null when nothing applies
 */
export const buildLatestRecordedFlowSheetValue = (field, entries) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const config = field?.flowSheet || {};
  const configured = new Set([
    ...(config.catalogItemIds || []),
    ...((config.flowItems || []).map(i => i?.id).filter(Boolean)),
  ]);
  if (configured.size === 0) return null;

  const staticValues = {};
  const sideStatus = {};
  // Newest first: keep the first row seen per (item, side) in case the
  // endpoint ever returns duplicates across '' / null sides
  const seen = new Set();
  const sorted = [...entries].sort((a, b) => (Number(b.recorded_at) || 0) - (Number(a.recorded_at) || 0));

  for (const e of sorted) {
    const itemId = e?.flow_item_id;
    if (!itemId || !configured.has(itemId)) continue;
    const side = (e.side === 'left' || e.side === 'right') ? e.side : '';
    const seenKey = `${itemId}|${side}`;
    if (seen.has(seenKey)) continue;
    seen.add(seenKey);

    let vj = e.value_json;
    if (typeof vj === 'string') {
      try { vj = JSON.parse(vj); } catch { vj = null; }
    }
    vj = vj && typeof vj === 'object' && !Array.isArray(vj) ? vj : {};

    const rawValue = e.value == null ? '' : String(e.value);
    if (rawValue === '' && !vj.status) continue;
    const statusOnly = !!vj.status && rawValue === String(vj.status);

    const cur = staticValues[itemId] || (staticValues[itemId] = {});
    if (side) {
      if (!statusOnly && rawValue !== '') cur[side] = rawValue;
      sideStatus[itemId] = sideStatus[itemId] || {};
      sideStatus[itemId][side] = vj.status || null;
    } else {
      if (Array.isArray(vj.selectedValues)) {
        cur.value = vj.selectedValues;
      } else if (!statusOnly && rawValue !== '') {
        cur.value = rawValue;
      }
      if (vj.status) cur.status = vj.status;
    }
    if (vj.componentScores && typeof vj.componentScores === 'object' && Object.keys(vj.componentScores).length > 0) {
      cur.componentScores = vj.componentScores;
    }
    if (vj.componentLabels && typeof vj.componentLabels === 'object' && Object.keys(vj.componentLabels).length > 0) {
      cur.componentLabels = vj.componentLabels;
    }
  }

  // Bilateral status (WNL/WFL) applies to the item, not a side: set it only
  // when every recorded side agrees (sides may come from different notes)
  for (const [itemId, sides] of Object.entries(sideStatus)) {
    const statuses = Object.values(sides);
    const first = statuses[0];
    if (first && statuses.every(st => st === first)) {
      staticValues[itemId].status = first;
    }
  }

  // Drop items that ended up empty
  for (const [itemId, v] of Object.entries(staticValues)) {
    const hasContent = v.status || v.left || v.right ||
      (Array.isArray(v.value) ? v.value.length > 0 : (v.value != null && v.value !== ''));
    if (!hasContent) delete staticValues[itemId];
  }
  if (Object.keys(staticValues).length === 0) return null;

  return { staticValues, exerciseRows: [], categoryComments: {}, itemGoals: {} };
};

/**
 * Apply carryforward rules to pre-populate fields from prior signed notes.
 * Returns the updated sectionsData with carried-forward values.
 *
 * @param {Object} tmpl - full template with sections[].fields[]
 * @param {Object} initialSections - sectionsData to fill (only empty fields touched)
 * @param {Object} noteContext - { patient_id, case_id, note_type } of the target note
 * @param {Object|null} encounterData - encounter (used for case_id fallback)
 * @param {Object|null} orgSettings - organization settings (kept for signature
 *   compatibility; exercise rows are no longer carried here, see the flow_sheet
 *   transform below)
 */
// eslint-disable-next-line no-unused-vars
export const applyCarryForward = async (tmpl, initialSections, noteContext, encounterData, orgSettings = null) => {
  if (!tmpl?.sections) return initialSections;

  // Collect fields that need carryforward
  const carryForwardFields = [];
  for (const section of tmpl.sections) {
    for (const field of section.fields || []) {
      if (!isDischargeActionField(field) && field.carryForward) {
        carryForwardFields.push({ sectionId: section.id, field });
      }
    }
  }

  if (carryForwardFields.length === 0) return initialSections;

  // Group by source type to batch API calls
  const sourceCache = {};

  const getSourceNote = async (cf, currentNote) => {
    const patientId = currentNote?.patient_id;
    const caseId = currentNote?.case_id || encounterData?.case_id;
    const noteType = currentNote?.note_type;

    // Determine effective scope: if scope is 'case' but no case exists, fall back to patient
    const effectiveScope = (cf.scope === 'case' && caseId) ? 'case' : 'patient';
    const scopeKey = effectiveScope === 'case' ? `case_${caseId}` : `patient_${patientId}`;

    // Source note-type set for the latest-signed lookup: a per-field override
    // (any array, even empty) wins, else the template default. Newest signed
    // note whose type is in the set wins, so a daily note whose template lacks
    // the field cannot shadow the last progress note or eval. Empty = any type.
    const usesNoteTypes = cf.source === 'latest_signed' || cf.source === 'latest_recorded';
    const sourceNoteTypes = usesNoteTypes
      ? (Array.isArray(cf.noteTypes) ? cf.noteTypes : (tmpl?.carry_forward_note_types || [])).filter(Boolean)
      : [];
    const typesKey = sourceNoteTypes.length > 0 ? `_types_${[...sourceNoteTypes].sort().join(',')}` : '';

    let cacheKey;
    if (cf.source === 'latest_signed') {
      cacheKey = `latest_${scopeKey}${typesKey}`;
    } else if (cf.source === 'initial_eval') {
      cacheKey = `initial_eval_${scopeKey}`;
    } else if (cf.source === 'latest_same_type') {
      cacheKey = `latest_${noteType}_${scopeKey}`;
    } else if (cf.source === 'latest_same_type_or_initial') {
      cacheKey = `latest_${noteType}_or_initial_${scopeKey}`;
    } else if (cf.source === 'latest_recorded') {
      // Non-flow_sheet fields configured with latest_recorded fall back to the
      // latest signed note (the entries-based source only exists for flow sheets)
      cacheKey = `latest_${scopeKey}${typesKey}`;
    }

    if (cacheKey && sourceCache[cacheKey]) return sourceCache[cacheKey];

    try {
      const filters = { signed: true, limit: 1 };
      if (effectiveScope === 'case') {
        filters.case_id = caseId;
      } else if (patientId) {
        filters.patient_id = patientId;
      }

      if (sourceNoteTypes.length > 0) {
        filters.note_types = sourceNoteTypes.join(',');
      }

      if (cf.source === 'initial_eval') {
        filters.note_type = 'initial_evaluation';
      } else if (cf.source === 'latest_same_type' && noteType) {
        filters.note_type = noteType;
      } else if (cf.source === 'latest_same_type_or_initial' && noteType) {
        // Try same type first, fall back to initial evaluation
        filters.note_type = noteType;
      }

      const notes = await apiService.getClinicalNotes(filters);
      const notesList = Array.isArray(notes) ? notes : notes?.items || [];
      let sourceNote = notesList.length > 0 ? notesList[0] : null;

      // Fallback: if latest_same_type_or_initial found nothing, try initial_evaluation
      if (!sourceNote && cf.source === 'latest_same_type_or_initial') {
        const fallbackFilters = { signed: true, limit: 1, note_type: 'initial_evaluation' };
        if (effectiveScope === 'case') {
          fallbackFilters.case_id = caseId;
        } else if (patientId) {
          fallbackFilters.patient_id = patientId;
        }
        const fallbackNotes = await apiService.getClinicalNotes(fallbackFilters);
        const fallbackList = Array.isArray(fallbackNotes) ? fallbackNotes : fallbackNotes?.items || [];
        sourceNote = fallbackList.length > 0 ? fallbackList[0] : null;
      }

      if (cacheKey) sourceCache[cacheKey] = sourceNote;
      return sourceNote;
    } catch {
      return null;
    }
  };

  // `latest_recorded` (flow_sheet only): newest non-empty flow_sheet_entry per
  // (item, side) across ALL prior notes in scope — eval + follow-ups merged —
  // instead of one source note's field value. One fetch per scope.
  const latestEntriesCache = {};
  const getLatestEntries = async (cf, currentNote) => {
    const patientId = currentNote?.patient_id;
    const caseId = currentNote?.case_id || encounterData?.case_id;
    const useCase = cf.scope === 'case' && caseId;
    const scope = useCase ? { case_id: caseId } : (patientId ? { patient_id: patientId } : null);
    if (!scope) return [];
    const key = JSON.stringify(scope);
    if (latestEntriesCache[key]) return latestEntriesCache[key];
    try {
      const res = await apiService.getLatestFlowSheetEntries(scope);
      const items = Array.isArray(res) ? res : (res?.items || []);
      latestEntriesCache[key] = items;
      return items;
    } catch {
      return [];
    }
  };

  // Process each carryforward field
  const updated = JSON.parse(JSON.stringify(initialSections));

  for (const { sectionId, field } of carryForwardFields) {
    // Guard each field independently so one malformed source value
    // can't abort carryforward for the rest of the note
    try {
      const cf = field.carryForward;
      if (cf.mode !== 'auto' && cf.mode !== 'auto_review') continue; // auto and auto_review pre-fill

      let carryValue;
      if (cf.source === 'latest_recorded' && field.type === 'flow_sheet') {
        const entries = await getLatestEntries(cf, noteContext);
        carryValue = buildLatestRecordedFlowSheetValue(field, entries);

        // Category comments and item goals live only in note field values, not
        // in flow_sheet_entry rows: take them from the latest signed note in
        // scope (honoring the source note-type set), same field id only.
        // Goals are limited to items this sheet configures.
        const sourceNote = await getSourceNote(cf, noteContext);
        const srcField = (sourceNote?.sections_data?.sections || [])
          .flatMap(s => s.fields || [])
          .find(f => f.fieldId === field.id);
        const srcVal = srcField?.value && typeof srcField.value === 'object' && !Array.isArray(srcField.value)
          ? srcField.value : null;
        const hasKeys = (o) => o && typeof o === 'object' && Object.keys(o).length > 0;
        if (srcVal && (hasKeys(srcVal.categoryComments) || hasKeys(srcVal.itemGoals))) {
          const configured = new Set([
            ...((field.flowSheet?.catalogItemIds) || []),
            ...((field.flowSheet?.flowItems || []).map(i => i?.id).filter(Boolean)),
          ]);
          const goals = Object.fromEntries(
            Object.entries(srcVal.itemGoals || {}).filter(([itemId]) => configured.has(itemId))
          );
          carryValue = {
            ...(carryValue || { staticValues: {}, exerciseRows: [], categoryComments: {}, itemGoals: {} }),
            categoryComments: { ...(srcVal.categoryComments || {}) },
            itemGoals: goals,
          };
        }
        if (!carryValue) continue;
      } else {
        const sourceNote = await getSourceNote(cf, noteContext);
        if (!sourceNote?.sections_data?.sections) continue;

        // Find the source field value. Candidates are tried in order - the
        // first with a non-null value wins (mirrors form population's
        // questionIds fallback). Legacy single sourceFieldId still honored.
        const candidateFieldIds = cf.sourceFieldIds?.length
          ? cf.sourceFieldIds
          : (cf.sourceFieldId ? [cf.sourceFieldId] : [field.id]);
        let sourceValue = null;

        const hasValue = (v) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0);

        outer:
        for (const candidateId of candidateFieldIds) {
          for (const srcSection of sourceNote.sections_data.sections) {
            const srcField = srcSection.fields?.find(f => f.fieldId === candidateId);
            if (srcField && hasValue(srcField.value)) {
              sourceValue = srcField.value;
              break outer;
            }
          }
        }

        if (sourceValue == null) continue;

        // Unwrap object values from data transfers (e.g. { content: "..." } → string)
        if (typeof sourceValue === 'object' && !Array.isArray(sourceValue) && 'content' in sourceValue) {
          sourceValue = sourceValue.content ?? '';
        }

        // Transform grid carryforward (e.g. eval goals → status update grid)
        carryValue = sourceValue;
        if (field.type === 'data_grid') {
          carryValue = remapGridRowsToColumns(sourceValue, field.columns);
        }

        // Transform cpt_code_entry carryforward
        if (field.type === 'cpt_code_entry' && Array.isArray(carryValue)) {
          // Strip L codes (orthotics/prosthetics) for every target note type:
          // a dispensed item is billed once and must never carry to the next note
          carryValue = carryValue.filter(entry => !String(entry.code || '').trim().toUpperCase().startsWith('L'));
          // Strip eval/re-eval codes unless the target is an initial evaluation;
          // every other note type (daily, progress, reassessment, re-eval,
          // discharge) picks its own eval code deliberately
          const targetNoteType = noteContext?.note_type || tmpl?.note_type;
          if (targetNoteType !== 'initial_evaluation') {
            carryValue = carryValue.filter(entry => !THERAPY_EVAL_CPT_CODES.has(entry.code));
          }
          // Reset session-specific billing fields and strip flow-sheet provenance —
          // carried rows become plain manual rows; the new note's reconcile re-links
          // them by code if its own flow sheet carries intervention assignments
          // Session-specific values are cleared to blank (null), not 0, so the
          // clinician must decide minutes/units before the new note can be signed.
          // Untimed codes are always 1 unit; indirect rows keep direct minutes at 0.
          // Charge is session-specific too: it is resolved live from the org fee
          // sources by the editor (useCptRates) and again at save/sign, so the
          // source note's price is never carried.
          carryValue = carryValue.map((row) => {
            const entry = { ...row };
            if (entry.timed === undefined) {
              entry.timed = TIMED_CPT_CODES.has(entry.code);
            }
            const isIndirect = entry.indirect === true;
            entry.minutes = isIndirect ? 0 : null;
            if (isIndirect) entry.indirect_minutes = null;
            entry.units = entry.timed && !isIndirect ? null : 1;
            entry.charge = 0;
            delete entry.source;
            delete entry.sourceLabel;
            delete entry.sourceFieldId;
            delete entry.exerciseNotes;
            return entry;
          });
        }

        // Transform flow_sheet carryforward: keep static values (MMT/ROM),
        // category comments, and item goals — but NOT exercise rows. Exercise
        // rows are populated by the editor from flow_sheet_entry records
        // (FlowSheetFieldEditor.loadPriorEntries), which is the only source that
        // honors discontinuation; copying a prior note's rows verbatim revived
        // discontinued exercises.
        if (field.type === 'flow_sheet' && carryValue && typeof carryValue === 'object' && !Array.isArray(carryValue)) {
          carryValue = {
            ...carryValue,
            exerciseRows: [],
          };
        }
      }

      // Apply to the target field in sectionsData
      const targetSection = updated.sections.find(s => s.sectionId === sectionId);
      if (!targetSection) continue;
      const targetField = targetSection.fields.find(f => f.fieldId === field.id);
      if (!targetField) continue;

      // Only fill if target is empty
      const isEmpty = !targetField.value ||
        (Array.isArray(targetField.value) && targetField.value.length === 0) ||
        targetField.value === '';
      if (isEmpty) {
        targetField.value = carryValue;
        // Track carryforward metadata for auto_review fields
        if (cf.mode === 'auto_review') {
          targetField.carriedForward = true;
          targetField.confirmedByProvider = false;
        }
      }
    } catch (err) {
      console.error(`Carryforward failed for field ${field?.id}:`, err);
    }
  }

  return updated;
};

/**
 * Apply form population if the template has any formPopulation-configured fields.
 * Fetches patient form responses (and reports if needed) and runs applyFormPopulation.
 * Only fills fields that are still empty after carryforward.
 */
/**
 * @param {Object} tmpl - note template
 * @param {Object} currentSectionsData
 * @param {string} patientId
 * @param {number|null} [caseId] - the note's case; scopes which form responses may populate
 */
export const applyFormPopulationIfNeeded = async (tmpl, currentSectionsData, patientId, caseId = null) => {
  const hasFormPopFields = tmpl.sections?.some(
    s => s.fields?.some(f => !isDischargeActionField(f) && f.formPopulation)
  );
  if (!hasFormPopFields || !patientId) return currentSectionsData;

  try {
    const patientFormResponses = await apiService.getPatientForms(patientId);
    if (!patientFormResponses?.length) return currentSectionsData;

    // Check if any fields use report-based population
    const hasReportFields = tmpl.sections?.some(
      s => s.fields?.some(f => !isDischargeActionField(f) && f.formPopulation?.source === 'report')
    );

    let formReports = undefined;
    if (hasReportFields) {
      try {
        formReports = await apiService.getPatientReports(patientId);
      } catch (err) {
        console.error('Failed to fetch form reports for population:', err);
      }
    }

    return applyFormPopulation(tmpl, currentSectionsData, patientFormResponses, formReports, caseId);
  } catch (err) {
    console.error('Form population failed:', err);
    return currentSectionsData;
  }
};

/**
 * Apply prefill rules from template field config.
 * Supports static, linked_record, body_region, and functional_area sources.
 * field.prefill can be a single rule (legacy) or an array of rules.
 * Only fills fields that are still empty after carryforward and form population.
 */
export const applyPrefill = (tmpl, currentSectionsData, context, activeBodyRegions = [], activeFunctionalAreas = []) => {
  if (!tmpl?.sections || !currentSectionsData?.sections) return currentSectionsData;
  const updated = JSON.parse(JSON.stringify(currentSectionsData));

  for (const section of tmpl.sections) {
    for (const field of section.fields || []) {
      if (isDischargeActionField(field) || !field.prefill) continue;
      const sd = updated.sections.find(s => s.sectionId === section.id);
      if (!sd) continue;
      let fe = sd.fields.find(f => f.fieldId === field.id);

      const fieldIsEmpty = !fe || fe.value == null || fe.value === '' || fe.value === false ||
        (Array.isArray(fe.value) && fe.value.length === 0);
      if (!fieldIsEmpty) continue;

      const rules = Array.isArray(field.prefill) ? field.prefill : [field.prefill];
      const matchingValues = [];

      for (const rule of rules) {
        if (rule.source === 'static') {
          if (rule.staticValue != null) matchingValues.push(rule.staticValue);
        } else if (rule.source === 'linked_record') {
          const record = context[rule.entity];
          let value = rule.property?.includes('.')
            ? rule.property.split('.').reduce((obj, key) => obj?.[key], record)
            : record?.[rule.property];
          if (value && typeof value === 'number' && field.type === 'date_composite') {
            value = new Date(value).toISOString().split('T')[0];
          }
          if (value != null) matchingValues.push(value);
        } else if (rule.source === 'body_region') {
          if (rule.bodyRegion && activeBodyRegions.includes(rule.bodyRegion) && rule.staticValue != null) {
            matchingValues.push(rule.staticValue);
          }
        } else if (rule.source === 'functional_area') {
          if (rule.functionalArea && activeFunctionalAreas.includes(rule.functionalArea) && rule.staticValue != null) {
            matchingValues.push(rule.staticValue);
          }
        }
      }

      if (matchingValues.length === 0) continue;

      let value;
      if (matchingValues.length === 1) {
        value = matchingValues[0];
        // Array-valued linked-record properties (e.g. plan_of_care.precautions)
        // targeting a text field must be joined, not dumped as a raw array
        if (Array.isArray(value) && (field.type === 'text' || field.type === 'textarea')) {
          value = value.filter(v => v != null && v !== '').map(String).join('\n');
        }
      } else if (field.type === 'text' || field.type === 'textarea') {
        value = matchingValues
          .map(v => (Array.isArray(v) ? v.filter(x => x != null && x !== '').map(String).join('\n') : v))
          .filter(v => v !== '')
          .join('\n\n');
      } else if (field.type === 'data_grid') {
        value = matchingValues.reduce((acc, v) => acc.concat(Array.isArray(v) ? v : []), []);
      } else {
        value = matchingValues[0];
      }

      if (fe) {
        fe.value = value;
        fe.prefillApplied = true;
      } else {
        sd.fields.push({ fieldId: field.id, value, prefillApplied: true });
      }
    }
  }
  return updated;
};

/**
 * Full enrichment pipeline: carryforward → form population → prefill.
 *
 * @param {Object} params
 * @param {Object} params.template - full template with sections
 * @param {Object} params.sectionsData - skeleton or partially-filled sections_data
 * @param {Object} params.note - { patient_id, case_id, note_type } (or full note)
 * @param {Object|null} params.encounter - encounter for case_id fallback
 * @param {Object} params.prefillContext - { encounter, appointment, patient, case, plan_of_care }
 * @param {string[]} [params.bodyRegions]
 * @param {string[]} [params.functionalAreas]
 * @returns {Promise<Object>} enriched sectionsData
 */
/**
 * Merge a server-delivered sections_data into local editor state without
 * losing unsaved local edits. Used when scribe documentation completes:
 * the server copy contains the backend's authoritative merge, but the
 * provider may have typed into fields since the last autosave.
 *
 * Dirtiness is exact, not emptiness-based: a field is "locally dirty" when
 * its local value differs from the last PERSISTED value. Dirty fields keep
 * the local value; everything else takes the server value. (The server doc
 * is complete — append-style merging would duplicate content.)
 *
 * @param {Object} localSections - current editor sections_data
 * @param {Object} serverSections - freshly fetched sections_data
 * @param {Object|null} lastSavedSections - sections_data at last successful save
 * @returns {Object} merged sections_data (server shape, local dirty values kept)
 */
export const mergeServerSections = (localSections, serverSections, lastSavedSections) => {
  if (!serverSections?.sections) return localSections;
  if (!localSections?.sections) return serverSections;

  const keyOf = (sectionId, fieldId) => `${sectionId}::${fieldId}`;

  const indexFields = (sectionsData) => {
    const map = new Map();
    for (const section of sectionsData?.sections || []) {
      for (const field of section.fields || []) {
        map.set(keyOf(section.sectionId, field.fieldId), field);
      }
    }
    return map;
  };

  const localMap = indexFields(localSections);
  const savedMap = indexFields(lastSavedSections);

  const merged = JSON.parse(JSON.stringify(serverSections));

  for (const section of merged.sections || []) {
    for (const field of section.fields || []) {
      const key = keyOf(section.sectionId, field.fieldId);
      const localField = localMap.get(key);
      if (!localField) continue;

      const savedField = savedMap.get(key);
      const localJson = JSON.stringify(localField.value ?? null);
      const savedJson = JSON.stringify(savedField?.value ?? null);

      if (localJson !== savedJson) {
        // Unsaved local edit wins over the server copy
        field.value = localField.value;
        if (localField.carriedForward !== undefined) field.carriedForward = localField.carriedForward;
        if (localField.confirmedByProvider !== undefined) field.confirmedByProvider = localField.confirmedByProvider;
      }
    }
  }

  // Local-only fields (e.g. rows the editor added that the server doesn't
  // know about yet) are preserved by appending them to their section.
  const mergedKeys = new Set();
  for (const section of merged.sections || []) {
    for (const field of section.fields || []) {
      mergedKeys.add(keyOf(section.sectionId, field.fieldId));
    }
  }
  for (const section of localSections.sections || []) {
    const target = (merged.sections || []).find(s => s.sectionId === section.sectionId);
    if (!target) continue;
    for (const field of section.fields || []) {
      if (!mergedKeys.has(keyOf(section.sectionId, field.fieldId))) {
        target.fields.push(field);
      }
    }
  }

  return merged;
};

export const enrichSectionsData = async ({
  template,
  sectionsData,
  note,
  encounter = null,
  prefillContext = {},
  bodyRegions = [],
  functionalAreas = [],
  orgSettings = null,
}) => {
  let result = await applyCarryForward(template, sectionsData, note, encounter, orgSettings);
  result = result || sectionsData;

  result = await applyFormPopulationIfNeeded(template, result, note?.patient_id, note?.case_id || encounter?.case_id || null);

  result = applyPrefill(template, result, prefillContext, bodyRegions, functionalAreas);

  return result;
};
